import React, {useEffect, useRef, useState} from 'react'
import {Outlet, useLocation} from 'react-router'
import {Link, NavLink, useNavigation, useRouteLoaderData} from 'react-router-dom'
import './layout.less'
import cx from 'classnames'
import {
  Layout as AntdLayout,
  Menu,
  MenuProps,
  theme,
  Button as AntdButton,
  ConfigProvider,
  Dropdown,
  message,
} from 'antd'
import {
  HomeOutlined,
  ProjectOutlined,
  UnorderedListOutlined,
  UserOutlined,
  GlobalOutlined,
  FlagOutlined,
  BookOutlined,
  MenuOutlined,
  EnvironmentOutlined,
} from '@ant-design/icons'
import {useAsyncValue} from '../../hooks/useAsyncValue'
import {IntlShape, useIntl} from 'react-intl'
import {SupportedLocale, useChangeLocale, useLocale} from '../LocalizationContext'
import {LogOutLink} from '../logOutLink/LogOutLink'
import {useOnClickOutside} from 'usehooks-ts'

const {Header, Sider, Content} = AntdLayout

async function getItems(
  intl: IntlShape,
  projects: {id: number; name: string}[]
): Promise<MenuProps['items']> {
  const leftMenuSections = [
    {name: intl.formatMessage({id: 'home'}), icon: HomeOutlined, route: '/'},
    {
      name: intl.formatMessage({id: 'projects'}),
      icon: ProjectOutlined,
      route: '/projects',
      // getSubItems: async () => {
      //   const projects = await projectsService.getUserProjects()
      //   return projects.map((project) => ({name: project.name, route: `/projects/${project.id}`}))
      // },
      getSubItems: async () => {
        return projects.map((project) => ({name: project.name, route: `/projects/${project.id}`}))
      },
    },
    {name: intl.formatMessage({id: 'todoLists'}), icon: UnorderedListOutlined, route: '/todos'},
    {name: intl.formatMessage({id: 'plants'}), icon: EnvironmentOutlined, route: '/plants'},
    {name: intl.formatMessage({id: 'journal'}), icon: BookOutlined, route: '/journal'},
    //{name: intl.formatMessage({id: 'snapshots'}), icon: BookOutlined, route: '/snapshots'}, snapshots are disabled
  ]

  return Promise.all(
    leftMenuSections.map(async (section, index) => {
      const key = String(index + 1)

      return {
        key: `sub${key}`,
        icon: React.createElement(section.icon),
        label: (
          <NavLink
            className={({isActive, isPending}) =>
              cx('nav-link', isActive && 'nav-link--active', isPending && 'nav-link--pending')
            }
            to={section.route}
          >
            {section.name}
          </NavLink>
        ),

        children:
          typeof section.getSubItems === 'function'
            ? (await section.getSubItems()).map((subItem, j) => {
                const subKey = index * 4 + j + 1
                return {
                  key: subKey,
                  label: (
                    <NavLink
                      className={({isActive, isPending}) =>
                        cx(
                          'nav-link',
                          isActive && 'nav-link--active',
                          isPending && 'nav-link--pending'
                        )
                      }
                      to={subItem.route}
                    >
                      {subItem.name}
                    </NavLink>
                  ),
                }
              })
            : undefined,
      }
    })
  )
}

export function Layout() {
  const intl = useIntl()
  const navigation = useNavigation()
  const [collapsed, setCollapsed] = useState(true)
  const {
    token: {colorBgContainer},
  } = theme.useToken()
  const siderRef = useRef(null)
  const collapseTriggerRef = useRef<HTMLButtonElement>(null)
  const location = useLocation()

  // on small screens the sider overlays the page, so hide it after every navigation
  useEffect(() => {
    setCollapsed(true)
  }, [location.key])

  useOnClickOutside(siderRef, (evt) => {
    if (
      collapseTriggerRef.current &&
      evt.target &&
      (evt.target === collapseTriggerRef.current ||
        collapseTriggerRef.current.contains(evt.target as any))
    ) {
      return
    }

    setCollapsed(true)
  })

  const {projectNames} = (useRouteLoaderData('root') as any) || {
    projectNames: [],
  }

  const {value: items, error} = useAsyncValue([], () => getItems(intl, projectNames), [
    intl.locale,
    projectNames,
  ])
  const changeLocale = useChangeLocale()
  const locale = useLocale()

  const itemsUserIcon: MenuProps['items'] = [
    {
      key: '1',
      label: <Link to="/user">{intl.formatMessage({id: 'userAccount'})}</Link>,
    },
    {
      key: '2',
      label: <LogOutLink />,
    },
  ]

  const languages: SupportedLocale[] = ['en', 'ru']

  const itemsLanguages: MenuProps['items'] = languages.map((l) => ({
    key: l,
    label: l,
    onClick: () => changeLocale(l),
    className: locale === l ? 'active' : '',
  }))

  return (
    <AntdLayout style={{minHeight: '100vh'}}>
      <ConfigProvider
        theme={{
          token: {
            colorLink: 'white',
          },
        }}
      >
        <Header style={{padding: 0}}>
          <div
            style={{
              display: 'flex',
              paddingLeft: 24,
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <AntdButton
              className="collapse-trigger"
              size="large"
              type="link"
              ref={collapseTriggerRef}
              onClick={() => setCollapsed((current) => !current)}
            >
              <MenuOutlined />
            </AntdButton>
            <div className="logo" style={{position: 'relative', overflow: 'hidden'}}>
              <Link to="/">
                <h1 style={{margin: 0, paddingLeft: 0, color: 'white'}}>TerraSketch</h1>
              </Link>
            </div>
            <div style={{flex: '1 1 0%', overflow: 'hidden'}}></div>
            <div
              style={{
                float: 'right',
                alignItems: 'center',
                paddingRight: '24px',
              }}
            >
              <Dropdown menu={{items: itemsLanguages}} placement="bottomLeft">
                <AntdButton type="link">
                  <GlobalOutlined />
                </AntdButton>
              </Dropdown>
              <Dropdown menu={{items: itemsUserIcon}} placement="bottomLeft">
                <AntdButton type="link">
                  <UserOutlined />
                </AntdButton>
              </Dropdown>
            </div>
          </div>
        </Header>
      </ConfigProvider>
      <AntdLayout>
        <Sider
          trigger={null}
          collapsed={false}
          style={{background: colorBgContainer}}
          className={cx(collapsed && 'side-menu--collapsed', 'side-menu')}
          ref={siderRef}
        >
          <Menu
            mode="inline"
            defaultSelectedKeys={['1']}
            defaultOpenKeys={['sub1']}
            style={{borderRight: 0}}
            items={items}
          />

          <Link className="policy-link" to="/policies">
            {intl.formatMessage({id: 'policy'})}
          </Link>
        </Sider>

        <Content
          className="page-content"
          style={{
            minHeight: 280,
            background: colorBgContainer,
          }}
        >
          <div className={navigation.state === 'loading' ? 'loading' : ''}>
            <Outlet />
          </div>
        </Content>
      </AntdLayout>
    </AntdLayout>
  )
}
